import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ApiError } from '../../services/apiClient';
import {
  chatbotInboxService,
  ChatSessionStatus,
  InboxListDebug,
  InboxMessage,
  InboxSessionDetail,
  InboxSessionRow,
} from '../../services/chatbotInboxService';
import ChatbotSubnav from './ChatbotSubnav';

const STATUS_TABS: { key: string; label: string }[] = [
  { key: 'open', label: 'Open' },
  { key: 'all', label: 'All engaged' },
  { key: 'human_requested', label: 'Human requested' },
  { key: 'ai_active', label: 'AI active' },
  { key: 'closed', label: 'Closed' },
  { key: 'removed', label: 'Removed' },
];

function statusLabel(status: ChatSessionStatus): string {
  const map: Record<ChatSessionStatus, string> = {
    ai_active: 'AI Active',
    human_requested: 'Human Requested',
    human_active: 'Human Active',
    closed: 'Closed',
  };
  return map[status] || status;
}

function statusBadgeClass(status: ChatSessionStatus): string {
  switch (status) {
    case 'human_requested':
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100';
    case 'human_active':
      return 'bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100';
    case 'closed':
      return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
    default:
      return 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-100';
  }
}

function MessageBubble({ m }: { m: InboxMessage }) {
  const isVisitor = m.sender_type === 'visitor' || m.role === 'user';
  const isHuman = m.sender_type === 'human';
  const isSystem = m.sender_type === 'system';
  return (
    <div className={`mb-3 ${isVisitor ? 'text-right' : 'text-left'}`}>
      {isHuman && (
        <div className="text-xs text-fg-secondary mb-1">Team member</div>
      )}
      <div
        className={`inline-block max-w-[85%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap ${
          isVisitor
            ? 'bg-primary text-white'
            : isHuman
              ? 'bg-emerald-600 text-white'
              : isSystem
                ? 'bg-app text-fg-secondary italic text-xs'
                : 'bg-app text-fg border border-border'
        }`}
      >
        {m.content}
      </div>
      <div className="text-[10px] text-fg-secondary mt-0.5">
        {new Date(m.created_at).toLocaleString()}
      </div>
    </div>
  );
}

const ChatInbox: React.FC = () => {
  const { sessionId } = useParams<{ sessionId?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState('open');
  const [rows, setRows] = useState<InboxSessionRow[]>([]);
  const [detail, setDetail] = useState<InboxSessionDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [listSince, setListSince] = useState<string | null>(null);
  const [listDebug, setListDebug] = useState<InboxListDebug | null>(null);
  const [listCount, setListCount] = useState(0);
  // Mobile-only: toggles the inline visitor-details section in the chat pane.
  const [showDetailsMobile, setShowDetailsMobile] = useState(false);

  const loadList = useCallback(async () => {
    try {
      const r = await chatbotInboxService.listInbox({ status: tab, page: 1, page_size: 50 });
      setRows(r.results || []);
      setListCount(r.count ?? 0);
      setListDebug(r.debug ?? null);
      setListSince(new Date().toISOString());
      setError('');
    } catch (e) {
      if (e instanceof ApiError) {
        setError(`${e.message} (HTTP ${e.status})`);
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load inbox');
      }
    } finally {
      setLoading(false);
    }
  }, [tab]);

  const loadDetail = useCallback(async (id: string) => {
    try {
      const d = await chatbotInboxService.getSession(id);
      setDetail(d);
      setError('');
    } catch (e) {
      setDetail(null);
      if (e instanceof ApiError) {
        setError(
          e.status === 404
            ? `Conversation not found (HTTP ${e.status}).`
            : `${e.message} (HTTP ${e.status})`
        );
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load conversation');
      }
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadList();
  }, [loadList]);

  useEffect(() => {
    setShowDetailsMobile(false);
    if (sessionId) loadDetail(sessionId);
    else setDetail(null);
  }, [sessionId, loadDetail]);

  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const poll = await chatbotInboxService.pollInbox(listSince || undefined);
        if (poll.new_activity_count > 0 || poll.human_requested_count > 0) {
          loadList();
        }
        if (sessionId) loadDetail(sessionId);
      } catch {
        /* ignore */
      }
    }, 5000);
    return () => clearInterval(t);
  }, [sessionId, listSince, loadList, loadDetail]);

  const selectSession = (id: string) => {
    navigate(`/chatbot/inbox/${id}`);
  };

  const refreshDetail = async () => {
    if (sessionId) await loadDetail(sessionId);
    await loadList();
  };

  const assignedToMe =
    detail?.assigned_user?.id && user?.id && detail.assigned_user.id === user.id;
  const canReply = detail?.status === 'human_active' && assignedToMe;
  const canTakeOver =
    detail &&
    (detail.status === 'human_requested' || detail.status === 'ai_active');
  const canRelease =
    detail?.status === 'human_active' &&
    (assignedToMe || user?.role === 'admin');
  const isAdmin = user?.role === 'admin';

  const handleTakeover = async () => {
    if (!sessionId) return;
    await chatbotInboxService.takeover(sessionId);
    await refreshDetail();
  };

  const handleRelease = async () => {
    if (!sessionId) return;
    await chatbotInboxService.release(sessionId);
    await refreshDetail();
  };

  const handleClose = async () => {
    if (!sessionId) return;
    await chatbotInboxService.close(sessionId);
    await refreshDetail();
  };

  const handleRemove = async () => {
    if (!sessionId || !window.confirm('Remove this chat from the inbox?')) return;
    await chatbotInboxService.remove(sessionId);
    navigate('/chatbot/inbox');
    await loadList();
  };

  const handleRestore = async () => {
    if (!sessionId) return;
    await chatbotInboxService.restore(sessionId);
    setTab('open');
    navigate('/chatbot/inbox');
  };

  const isRemovedSession = detail?.removed_from_inbox || tab === 'removed';

  const handleReply = async () => {
    if (!sessionId || !reply.trim()) return;
    setSending(true);
    try {
      const d = await chatbotInboxService.reply(sessionId, reply.trim());
      setDetail(d);
      setReply('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  // Visitor details body — reused by the desktop right pane and the mobile
  // collapsible section inside the chat pane.
  const visitorDetails = detail ? (
    <dl className="space-y-2 text-fg-secondary">
      <div>
        <dt className="text-xs uppercase tracking-wide">Name</dt>
        <dd className="text-fg">{detail.visitor_name || '—'}</dd>
      </div>
      <div>
        <dt className="text-xs uppercase tracking-wide">Email</dt>
        <dd className="text-fg break-all">{detail.visitor_email || '—'}</dd>
      </div>
      <div>
        <dt className="text-xs uppercase tracking-wide">Phone</dt>
        <dd className="text-fg">{detail.visitor_phone || '—'}</dd>
      </div>
      <div>
        <dt className="text-xs uppercase tracking-wide">Company</dt>
        <dd className="text-fg">{detail.visitor_company || '—'}</dd>
      </div>
      <div>
        <dt className="text-xs uppercase tracking-wide">Page</dt>
        <dd className="text-fg break-all text-xs">{detail.page_url || '—'}</dd>
      </div>
      {detail.crm_lead_id && (
        <div>
          <dt className="text-xs uppercase tracking-wide">CRM lead</dt>
          <dd className="text-fg text-xs font-mono">{detail.crm_lead_id}</dd>
        </div>
      )}
    </dl>
  ) : (
    <p className="text-fg-secondary">Select a conversation.</p>
  );

  return (
    <div className="p-3 sm:p-6 flex flex-col h-[calc(100vh-4rem)] max-w-[1400px]">
      <ChatbotSubnav />
      <h1 className="text-2xl font-semibold text-fg mb-4">Chat Inbox</h1>
      {error && (
        <div className="mb-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200 text-sm">
          {error}
        </div>
      )}
      <div className="flex gap-2 mb-3 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-sm rounded-md ${
              tab === t.key
                ? 'bg-primary text-white'
                : 'bg-surface border border-border text-fg-secondary hover:bg-app'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="flex flex-1 min-h-0 gap-4">
        <div
          className={`${sessionId ? 'hidden md:flex' : 'flex'} w-full md:w-72 md:shrink-0 flex-col border border-border rounded-lg bg-surface overflow-hidden`}
        >
          {loading ? (
            <p className="p-4 text-sm text-fg-secondary">Loading…</p>
          ) : rows.length === 0 ? (
            <div className="p-4 text-sm text-fg-secondary space-y-2">
              <p>No conversations in this filter.</p>
              {listCount === 0 &&
                listDebug &&
                listDebug.removed_engaged_count > 0 &&
                listDebug.all_sessions_count === 0 && (
                  <p className="text-amber-800 dark:text-amber-200">
                    {listDebug.removed_engaged_count} engaged conversation
                    {listDebug.removed_engaged_count === 1 ? ' was' : 's were'} removed from the
                    inbox. Open the <strong>Removed</strong> tab to review and restore them.
                  </p>
                )}
              {listCount === 0 &&
                listDebug &&
                listDebug.legacy_engaged_count > 0 &&
                listDebug.all_sessions_count > 0 &&
                listDebug.visible_engaged_count === 0 && (
                  <p className="text-amber-800 dark:text-amber-200">
                    Engaged sessions exist but none match this tab. Try{' '}
                    <strong>All engaged</strong> or <strong>Closed</strong>.
                  </p>
                )}
              {listCount === 0 &&
                listDebug &&
                listDebug.legacy_engaged_count === 0 &&
                listDebug.all_sessions_count === 0 && (
                  <p>
                    No chat sessions yet. Conversations appear here after a visitor sends a
                    message or submits contact info on your site.
                  </p>
                )}
              {listCount === 0 &&
                listDebug &&
                listDebug.all_sessions_count > 0 &&
                listDebug.legacy_engaged_count === 0 && (
                  <p>
                    Sessions exist but none are engaged yet (no visitor message or lead). The widget
                    only creates inbox rows after a visitor sends a message or submits contact info.
                  </p>
                )}
            </div>
          ) : (
            <ul className="overflow-y-auto flex-1">
              {rows.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => selectSession(r.id)}
                    className={`w-full text-left p-3 border-b border-border hover:bg-app ${
                      sessionId === r.id ? 'bg-primary/10' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between gap-1 mb-1">
                      <span
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${statusBadgeClass(r.status)}`}
                      >
                        {statusLabel(r.status)}
                      </span>
                      <span className="text-[10px] text-fg-secondary">
                        {r.last_message_at
                          ? new Date(r.last_message_at).toLocaleDateString()
                          : '—'}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-fg truncate">
                      {r.visitor_name || r.visitor_email || 'Visitor'}
                    </p>
                    <p className="text-xs text-fg-secondary truncate mt-0.5">
                      {r.last_preview || r.page_url || '—'}
                    </p>
                    {r.assignee_email && (
                      <p className="text-[10px] text-fg-secondary mt-1">{r.assignee_email}</p>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          className={`${sessionId ? 'flex' : 'hidden md:flex'} flex-1 flex-col min-w-0 border border-border rounded-lg bg-surface overflow-hidden`}
        >
          {!sessionId && (
            <p className="p-6 text-fg-secondary text-sm">Select a conversation to view the thread.</p>
          )}
          {sessionId && detail && (
            <>
              <div className="p-3 border-b border-border flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/chatbot/inbox')}
                  className="md:hidden text-xs px-2 py-1 rounded border border-border"
                >
                  ← Conversations
                </button>
                <button
                  type="button"
                  onClick={() => setShowDetailsMobile((v) => !v)}
                  aria-expanded={showDetailsMobile}
                  className="md:hidden text-xs px-2 py-1 rounded border border-border"
                >
                  ⓘ Details
                </button>
                <span
                  className={`text-xs font-medium px-2 py-1 rounded ${statusBadgeClass(detail.status)}`}
                >
                  {statusLabel(detail.status)}
                </span>
                {canTakeOver && !isRemovedSession && (
                  <button
                    type="button"
                    onClick={handleTakeover}
                    className="text-xs px-2 py-1 rounded bg-primary text-white"
                  >
                    Take over
                  </button>
                )}
                {canRelease && !isRemovedSession && (
                  <button
                    type="button"
                    onClick={handleRelease}
                    className="text-xs px-2 py-1 rounded border border-border"
                  >
                    Release to AI
                  </button>
                )}
                {detail.status !== 'closed' && !isRemovedSession && (
                  <button
                    type="button"
                    onClick={handleClose}
                    className="text-xs px-2 py-1 rounded border border-border"
                  >
                    Close
                  </button>
                )}
                {isAdmin && isRemovedSession && (
                  <button
                    type="button"
                    onClick={handleRestore}
                    className="text-xs px-2 py-1 rounded bg-primary text-white ml-auto"
                  >
                    Restore to inbox
                  </button>
                )}
                {isAdmin && !isRemovedSession && (
                  <button
                    type="button"
                    onClick={handleRemove}
                    className="text-xs px-2 py-1 rounded text-red-700 border border-red-300 ml-auto"
                  >
                    Remove
                  </button>
                )}
              </div>
              {showDetailsMobile && (
                <div className="md:hidden p-3 border-b border-border bg-app/40">
                  <h2 className="font-semibold text-fg mb-2 text-sm">Visitor</h2>
                  {visitorDetails}
                </div>
              )}
              <div className="flex-1 overflow-y-auto p-4">
                {(detail.messages || []).map((m) => (
                  <MessageBubble key={m.id} m={m} />
                ))}
              </div>
              <div className="p-3 border-t border-border">
                {isRemovedSession && (
                  <p className="text-xs text-fg-secondary mb-2">
                    This conversation was removed from the inbox. Restore it to reply or manage
                    again.
                  </p>
                )}
                {detail.status === 'ai_active' && !isRemovedSession && (
                  <p className="text-xs text-fg-secondary mb-2">Take over to reply manually.</p>
                )}
                {detail.status === 'human_active' && !assignedToMe && (
                  <p className="text-xs text-fg-secondary mb-2">
                    Assigned to {detail.assigned_user?.email || 'another agent'}.
                  </p>
                )}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    disabled={!canReply || sending}
                    placeholder={canReply ? 'Type a reply…' : 'Reply disabled'}
                    className="flex-1 px-3 py-2 text-sm border border-border rounded-lg bg-app text-fg"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && canReply) handleReply();
                    }}
                  />
                  <button
                    type="button"
                    disabled={!canReply || sending || !reply.trim()}
                    onClick={handleReply}
                    className="px-4 py-2 text-sm rounded-lg bg-primary text-white disabled:opacity-50"
                  >
                    Send
                  </button>
                </div>
              </div>
            </>
          )}
          {sessionId && !detail && !error && (
            <p className="p-6 text-fg-secondary text-sm">Loading conversation…</p>
          )}
        </div>

        <div className="hidden md:block w-56 shrink-0 border border-border rounded-lg bg-surface p-4 text-sm">
          <h2 className="font-semibold text-fg mb-3">Visitor</h2>
          {visitorDetails}
        </div>
      </div>
    </div>
  );
};

export default ChatInbox;
